import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Uppy from '@uppy/core';
import { message } from 'antd';
import classNames from 'classnames';
import { fabric } from 'fabric';
import { useProxyGrant } from './use-proxy-grant';
import { WEBSITE_SNAPSHOT_ATTACHMENT_NAME } from '@config/constants';
import {
  ProxyBridge,
  type ProxyParentBridge,
  type ProxyParentToChildMessage
} from '@website-proxy/frameBridge';
import type { AssetVersionItemDto } from '@api/Api';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import {
  ScreenCaptureController,
  useScreenCapture
} from '@pages/MediaViewer/AssetViewer/WebsiteCard/use-screen-capture';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas';
import {
  WEBSITE_COORDINATE_TRANSITION_FLAG,
  WEBSITE_HISTORY_REBASE_EVENT
} from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useCanvasHistory';
import { nonNull } from '@helpers/non-null';
import { useTypedSelector } from '@hooks';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import { useResponsive } from '@hooks/useResponsive';
import { CURSOR_DRAW_TOOL } from '@pages/MediaViewer/helpers/drawToolShape';

type HistoryEntry = { url: string; state: unknown; docId: number };

type HistoryState = {
  entries: HistoryEntry[];
  idx: number;
  currentDocId: number;
  nextDocId: number;
};

type SnapshotState =
  | {
      kind: 'object-url';
      url: string;
      coord: { width: number; height: number };
    }
  | {
      kind: 'attachment-url';
      url: string;
      coord: { width: number; height: number };
    };

export type Viewport = 'desktop' | 'tablet' | 'mobile';

const VIEWPORT_SIZES: Record<Viewport, number> = {
  desktop: 1024,
  tablet: 768,
  mobile: 390
};

type ViewportAvailability = Record<Viewport, boolean>;

type WebsiteAssetVersion = AssetVersionItemDto & {
  metadata: { type: 'website' };
};

interface UseWebsiteCardControllerResult {
  frameElement: React.ReactElement;
  faviconUrl: string | null;
  clearFavicon: () => void;
  currentUrl: string;
  viewport: Viewport;
  viewportAvailability: ViewportAvailability;
  setViewport: (viewport: Viewport) => void;
  canBack: boolean;
  canForward: boolean;
  isLoading: boolean;
  loadingButton: 'back' | 'forward' | 'reload' | null;
  back: () => void;
  forward: () => void;
  reload: () => void;
  hardReload: () => void;
  openInNewTab: () => void;
  copyUrl: () => void;
  snapshot: boolean;
  takingSnapshot: boolean;
  cancelSnapshot: () => void;
}

interface UseWebsiteCardControllerOptions {
  assetVersion: WebsiteAssetVersion;
  uppy: Uppy;
  snapshotDisabled?: boolean;
}

export function useWebsiteCardController({
  assetVersion,
  uppy,
  snapshotDisabled
}: UseWebsiteCardControllerOptions): UseWebsiteCardControllerResult {
  const { url, proxyUrl } = assetVersion.metadata;
  const frameRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const bridgeRef = useRef<ProxyParentBridge | null>(null);
  const canvasOverlayRef = useRef<HTMLDivElement | null>(null);

  const grant = useProxyGrant(assetVersion.id, proxyUrl);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [pageTitle, setPageTitle] = useState<string>(assetVersion.name);
  const [error, setError] = useState<string | null>(null);
  const [viewport, _setViewport] = useState<Viewport>('desktop');
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const viewportAvailability = useMemo<ViewportAvailability>(
    () => ({
      desktop:
        availableWidth === null || availableWidth >= VIEWPORT_SIZES.desktop,
      tablet:
        availableWidth === null || availableWidth >= VIEWPORT_SIZES.tablet,
      mobile: true
    }),
    [availableWidth]
  );
  const [history, setHistoryState] = useState<HistoryState>({
    entries: [],
    idx: -1,
    currentDocId: 0,
    nextDocId: 1
  });
  const historyRef = useRef(history);
  const [loadingButton, setLoadingButton] = useState<
    'back' | 'forward' | 'reload' | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    shapesDrawingTools,
    commentsList,
    selectedCommentId,
    isMobileDrawingToolbarOpen,
    dismissAnnotation,
    setZoomFactor,
    setWebsitePageUrl
  } = useAssetViewerState();
  const { isMdWidth } = useResponsive();
  const isNewComment = ['new', 'reply'].includes(selectedCommentId ?? '');
  const isDrawingOnCanvas =
    shapesDrawingTools.shape !== CURSOR_DRAW_TOOL &&
    (!isMdWidth || isMobileDrawingToolbarOpen);
  const hasAnnotations = useHasAnnotations();
  const snapshotRequested = isNewComment && hasAnnotations && !snapshotDisabled;

  const highlightSnapshotDisabled =
    isNewComment && hasAnnotations && snapshotDisabled;

  const startScreenCapture = useScreenCapture();
  const screenCaptureAbortController = useRef(new AbortController());
  const screenCapture = useRef<Promise<ScreenCaptureController> | null>(null);
  const screenCaptureSnapshotCompleted = useRef(false);
  const [snapshot, setSnapshot] = useState<SnapshotState | null>(null);
  const [takingSnapshot, setTakingSnapshot] = useState(false);
  const [loadingAttachmentSnapshot, setLoadingAttachmentSnapshot] =
    useState(false);
  const fabricContext = useContext(FabricContext);

  const selectedComment = useMemo(() => {
    if (!selectedCommentId) return null;
    if (selectedCommentId === 'new' || selectedCommentId === 'reply') {
      return null;
    }
    return commentsList.find((comment) => comment.id === selectedCommentId);
  }, [commentsList, selectedCommentId]);

  const selectedCommentSnapshotUrl = useMemo(() => {
    const snapshotAttachment = selectedComment?.attachments?.find((file) => {
      return file.name === WEBSITE_SNAPSHOT_ATTACHMENT_NAME && !!file.url;
    });
    return snapshotAttachment?.url ?? null;
  }, [selectedComment]);

  const websitePageUrl = selectedCommentSnapshotUrl
    ? selectedComment?.websitePageUrl ?? ''
    : currentUrl;

  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const _syncCanvasSize = () => {
    const { canvas } = fabricContext;
    const mount = mountRef.current;
    const overlay = canvasOverlayRef.current;
    if (!canvas || !mount || !overlay) return;

    const mountRect = mount.getBoundingClientRect();
    const currentSnapshot = snapshotRef.current;
    let left = 0;
    let top = 0;
    let { width } = mountRect;
    let { height } = mountRect;
    let scale = 1;

    if (currentSnapshot) {
      const { width: sourceWidth, height: sourceHeight } =
        currentSnapshot.coord;
      if (!sourceWidth || !sourceHeight || !width || !height) return;
      scale = Math.min(width / sourceWidth, height / sourceHeight);
      width = sourceWidth * scale;
      height = sourceHeight * scale;
      left = (mountRect.width - width) / 2;
      top = (mountRect.height - height) / 2;
    } else {
      const iframeRect = iframeRef.current?.getBoundingClientRect();
      if (iframeRect) {
        left = iframeRect.left - mountRect.left;
        top = iframeRect.top - mountRect.top;
        width = iframeRect.width;
        height = iframeRect.height;
      }
    }

    const roundedWidth = Math.max(1, Math.round(width));
    const roundedHeight = Math.max(1, Math.round(height));
    overlay.style.left = `${left}px`;
    overlay.style.top = `${top}px`;
    overlay.style.width = `${roundedWidth}px`;
    overlay.style.height = `${roundedHeight}px`;
    canvas.setDimensions({ width: roundedWidth, height: roundedHeight });
    canvas.setViewportTransform([10 * scale, 0, 0, 10 * scale, 0, 0]);
    canvas.calcOffset();
    canvas.getObjects().forEach((object) => object.setCoords());
    canvas.requestRenderAll();
    setZoomFactor(scale);
  };
  const syncCanvasSize = useRef(_syncCanvasSize);
  syncCanvasSize.current = _syncCanvasSize;

  const sendCommand = useRef((command: ProxyParentToChildMessage) => {
    bridgeRef.current?.send(command);
  });

  const updateHistory = useRef((next: HistoryState) => {
    const lengthChanged =
      next.entries.length !== historyRef.current.entries.length;
    historyRef.current = next;
    setHistoryState(next);
    if (lengthChanged) {
      sendCommand.current({
        type: 'stack-length',
        length: Math.max(1, next.entries.length)
      });
    }
  });

  const traverseTo = useRef(
    (newIdx: number, initiator: 'back' | 'forward' | null = null) => {
      const cur = historyRef.current;
      if (newIdx < 0 || newIdx >= cur.entries.length || newIdx === cur.idx) {
        return;
      }
      const target = cur.entries[newIdx];
      const sameDoc = target.docId === cur.currentDocId;
      setCurrentUrl(target.url);
      if (sameDoc) {
        sendCommand.current({
          type: 'traverse-same-doc',
          url: target.url,
          state: target.state
        });
        updateHistory.current({ ...cur, idx: newIdx });
      } else {
        if (initiator) setLoadingButton(initiator);
        setIsLoading(true);
        sendCommand.current({ type: 'load', url: target.url });
        updateHistory.current({
          ...cur,
          idx: newIdx,
          currentDocId: target.docId
        });
      }
    }
  );

  const canBack = history.idx > 0;
  const canForward = history.idx < history.entries.length - 1;

  const prevAssetName = useRef(assetVersion.name);
  if (prevAssetName.current !== assetVersion.name) {
    prevAssetName.current = assetVersion.name;
    setPageTitle(assetVersion.name);
  }

  const prevUrl = useRef(url);
  if (prevUrl.current !== url) {
    prevUrl.current = url;
    setCurrentUrl(url);
  }

  const prevProxyUrl = useRef(proxyUrl);
  if (prevProxyUrl.current !== proxyUrl) {
    prevProxyUrl.current = proxyUrl;
    historyRef.current = {
      entries: [],
      idx: -1,
      currentDocId: 0,
      nextDocId: 1
    };
    setHistoryState(historyRef.current);
    setFaviconUrl(null);
    setError(null);
    setLoadingButton(null);
    setIsLoading(true);
  }

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !grant.ready || !!error) {
      return;
    }

    let iframe = iframeRef.current;
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-forms allow-popups-to-escape-sandbox allow-presentation'
      );
      mount.prepend(iframe);
      iframeRef.current = iframe;
    } else if (iframe.parentElement !== mount) {
      mount.prepend(iframe);
    }

    if (iframe.title !== pageTitle) {
      iframe.title = pageTitle;
    }

    if (viewport === 'desktop') {
      iframe.style.removeProperty('width');
    } else {
      const viewportWidth =
        viewport === 'mobile' && availableWidth !== null
          ? Math.min(VIEWPORT_SIZES.mobile, availableWidth)
          : VIEWPORT_SIZES[viewport];
      iframe.style.width = `${viewportWidth}px`;
    }

    if (iframe.getAttribute('src') !== proxyUrl) {
      iframe.src = proxyUrl;
      if (bridgeRef.current) {
        bridgeRef.current.destroy();
      }
      const bridge = ProxyBridge.forParent(
        () => iframeRef.current?.contentWindow ?? null
      );
      bridgeRef.current = bridge;
    }
  }, [availableWidth, grant.ready, error, pageTitle, proxyUrl, viewport]);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const syncAvailableWidth = () => {
      const nextWidth = frame.getBoundingClientRect().width;
      if (nextWidth <= 0) return;
      setAvailableWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth
      );
    };
    const resizeObserver = new ResizeObserver(syncAvailableWidth);
    resizeObserver.observe(frame);
    syncAvailableWidth();
    return () => resizeObserver.disconnect();
  }, []);

  useLayoutEffect(() => {
    const mount = mountRef.current;
    const iframe = iframeRef.current;
    const canvas = fabricContext.canvas ?? null;
    if (!mount || !canvas) return;
    const resizeObserver = new ResizeObserver(syncCanvasSize.current);
    resizeObserver.observe(mount);
    if (iframe) resizeObserver.observe(iframe);
    syncCanvasSize.current();
    return () => {
      resizeObserver.disconnect();
    };
  }, [fabricContext.canvas, snapshot, grant.ready, viewport]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !grant.ready || !!error) {
      return;
    }
    const bridge = nonNull(bridgeRef.current);
    const unsubscribes = [
      bridge.subscribe('service-worker-not-supported', () => {
        setError('Service worker is not supported or failed to install.');
      }),
      bridge.subscribe('service-worker-install-failed', () => {
        setError('Service worker is not supported or failed to install.');
      }),
      bridge.subscribe('navigate', (data) => {
        setCurrentUrl(data.url);
        const cur = historyRef.current;
        let { currentDocId, nextDocId } = cur;
        let entryDocId: number;

        if (data.sameDoc) {
          entryDocId = currentDocId;
        } else {
          entryDocId = nextDocId;
          nextDocId += 1;
          currentDocId = entryDocId;
        }

        let newEntries: HistoryEntry[];
        let newIdx: number;
        if (data.navigationType === 'replace') {
          if (cur.entries.length === 0) {
            newEntries = [
              { url: data.url, state: data.state, docId: entryDocId }
            ];
            newIdx = 0;
          } else {
            newEntries = cur.entries.slice();
            newEntries[cur.idx] = {
              url: data.url,
              state: data.state,
              docId: entryDocId
            };
            newIdx = cur.idx;
          }
        } else {
          const trunc = cur.idx < 0 ? [] : cur.entries.slice(0, cur.idx + 1);
          trunc.push({
            url: data.url,
            state: data.state,
            docId: entryDocId
          });
          newEntries = trunc;
          newIdx = trunc.length - 1;
        }

        updateHistory.current({
          entries: newEntries,
          idx: newIdx,
          currentDocId,
          nextDocId
        });

        if (!data.sameDoc) {
          setIsLoading(true);
        }
        if (!data.sameDoc && !data.letThrough) {
          sendCommand.current({ type: 'load', url: data.url });
        }
      }),
      bridge.subscribe('loaded', (data) => {
        setCurrentUrl(data.url);
        setIsLoading(false);
        setLoadingButton(null);
        const cur = historyRef.current;
        if (cur.entries.length === 0) {
          const docId = cur.nextDocId;
          updateHistory.current({
            entries: [{ url: data.url, state: null, docId }],
            idx: 0,
            currentDocId: docId,
            nextDocId: docId + 1
          });
          return;
        }
        const entry = cur.entries[cur.idx];
        if (entry && entry.url !== data.url) {
          const next = cur.entries.slice();
          next[cur.idx] = { ...entry, url: data.url };
          updateHistory.current({ ...cur, entries: next });
        }
        if (entry && entry.state != null) {
          sendCommand.current({
            type: 'restore-state',
            state: entry.state
          });
        }
      }),
      bridge.subscribe('traverse-request', (data) => {
        const delta = Math.trunc(Number(data.delta) || 0);
        if (delta === 0) return;
        const cur = historyRef.current;
        const newIdx = Math.max(
          0,
          Math.min(cur.entries.length - 1, cur.idx + delta)
        );
        traverseTo.current(newIdx);
      }),
      bridge.subscribe('favicon', (data) => {
        setFaviconUrl(data.href);
      }),
      bridge.subscribe('title', (data) => {
        setPageTitle(data.title);
      }),
      bridge.subscribe('keydown', ({ type: _, ...data }) => {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            ...data,
            view: window,
            bubbles: true,
            cancelable: true,
            composed: true
          })
        );
      })
    ];
    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [grant.ready, error, proxyUrl]);

  const back = useCallback(() => {
    if (!canBack) return;
    traverseTo.current(historyRef.current.idx - 1, 'back');
  }, [canBack]);

  const forward = useCallback(() => {
    if (!canForward) return;
    traverseTo.current(historyRef.current.idx + 1, 'forward');
  }, [canForward]);

  const reload = useCallback(() => {
    setLoadingButton('reload');
    setIsLoading(true);
    sendCommand.current({ type: 'reload' });
  }, []);

  const hardReload = useCallback(() => {
    setLoadingButton('reload');
    setIsLoading(true);
    sendCommand.current({ type: 'clear-site-data' });
  }, []);

  const openInNewTab = useCallback(() => {
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
  }, [currentUrl]);

  const copyUrl = useCallback(() => {
    navigator.clipboard?.writeText(currentUrl).catch(() => {
      // clipboard may be unavailable in non-secure contexts; ignore
    });
  }, [currentUrl]);

  useLayoutEffect(() => {
    return () => {
      screenCaptureAbortController.current.abort();
    };
  }, []);

  const compareVersions = useTypedSelector(
    ({ modal }) => !!modal.compareVersionsModal?.visible
  );
  useEffect(() => {
    if (!compareVersions) return;
    // Ensure screen capture on the main asset viewer is aborted when the compare versions modal is opened,
    // so that the user can take a snapshot of the website in the compare versions modal instead.
    screenCaptureAbortController.current.abort();
  }, [compareVersions]);

  const _cancelAnnotations = () => {
    dismissAnnotation();
    fabricContext.clearCanvas();
  };
  const cancelAnnotations = useRef(_cancelAnnotations);
  cancelAnnotations.current = _cancelAnnotations;

  const applyViewport = useCallback((viewport: Viewport) => {
    _setViewport(viewport);
    cancelAnnotations.current();
  }, []);

  const setViewport = useCallback(
    (nextViewport: Viewport) => {
      if (!viewportAvailability[nextViewport]) return;
      applyViewport(nextViewport);
    },
    [applyViewport, viewportAvailability]
  );

  useLayoutEffect(() => {
    if (viewportAvailability[viewport]) return;
    applyViewport(viewportAvailability.tablet ? 'tablet' : 'mobile');
  }, [applyViewport, viewport, viewportAvailability]);

  const setSnapshotState = useCallback(
    (next: SnapshotState | null) => {
      setSnapshot((prev) => {
        if (prev?.kind === 'object-url') {
          URL.revokeObjectURL(prev.url);
        }
        return next;
      });
    },
    [setSnapshot]
  );

  const pauseAnimations = !!snapshot || shapesDrawingTools.shape !== 'cursor';
  useEffect(() => {
    const bridge = bridgeRef.current;
    if (!bridge) return;
    if (!pauseAnimations) return;
    bridge.send({ type: 'pause-animations' });
    return () => {
      bridge.send({ type: 'resume-animations' });
    };
  }, [pauseAnimations]);

  useLayoutEffect(() => {
    if (snapshotRequested || takingSnapshot) return;
    if (!selectedCommentSnapshotUrl) {
      setLoadingAttachmentSnapshot(false);
      if (snapshotRef.current?.kind === 'attachment-url') {
        setSnapshotState(null);
      }
      return;
    }

    let cancelled = false;
    const image = new Image();
    setLoadingAttachmentSnapshot(true);
    if (
      snapshotRef.current?.kind === 'attachment-url' &&
      snapshotRef.current.url !== selectedCommentSnapshotUrl
    ) {
      setSnapshotState(null);
    }
    image.onload = () => {
      if (cancelled) return;
      setSnapshotState({
        kind: 'attachment-url',
        url: selectedCommentSnapshotUrl,
        coord: {
          width: image.naturalWidth,
          height: image.naturalHeight
        }
      });
      setLoadingAttachmentSnapshot(false);
    };
    image.onerror = () => {
      if (cancelled) return;
      setSnapshotState(null);
      setLoadingAttachmentSnapshot(false);
    };
    image.src = selectedCommentSnapshotUrl;
    return () => {
      cancelled = true;
    };
  }, [
    selectedCommentSnapshotUrl,
    snapshotRequested,
    takingSnapshot,
    setSnapshotState
  ]);

  const prevSnapshotRequested = useRef(snapshotRequested);
  const uppyRef = useRef(uppy);
  uppyRef.current = uppy;

  const currentUrlRef = useRef(currentUrl);
  currentUrlRef.current = currentUrl;

  const canvasRef = useRef(fabricContext.canvas);
  canvasRef.current = fabricContext.canvas;
  const snapshotTransitionIdRef = useRef(0);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || takingSnapshot) return;
    (canvas as any)[WEBSITE_COORDINATE_TRANSITION_FLAG] = false;
  }, [takingSnapshot, snapshot]);

  useLayoutEffect(() => {
    if (prevSnapshotRequested.current === snapshotRequested) {
      return;
    }
    prevSnapshotRequested.current = snapshotRequested;
    if (!snapshotRequested) {
      setSnapshotState(null);
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe) return;

    const transitionId = ++snapshotTransitionIdRef.current;
    setTakingSnapshot(true);
    setSnapshotState(null);

    const canvas = canvasRef.current;
    if (!canvas) {
      setTakingSnapshot(false);
      cancelAnnotations.current();
      return;
    }
    (canvas as any)[WEBSITE_COORDINATE_TRANSITION_FLAG] = true;
    canvas.discardActiveObject();
    canvas.requestRenderAll();

    let controllerPromise = screenCapture.current;
    if (!controllerPromise) {
      const abortController = new AbortController();
      screenCaptureAbortController.current = abortController;
      screenCaptureSnapshotCompleted.current = false;
      const { signal } = abortController;
      signal.addEventListener('abort', () => {
        screenCapture.current = null;
        if (!screenCaptureSnapshotCompleted.current) {
          cancelAnnotations.current();
        }
      });
      controllerPromise = startScreenCapture(iframe, signal)
        .then((controller) => {
          controller.onStop(() => abortController.abort());
          return controller;
        })
        .catch((err) => {
          abortController.abort();
          throw err;
        });
      screenCapture.current = controllerPromise;
    }

    const abortController = new AbortController();
    const takeSnapshot = async () => {
      try {
        const controller = await controllerPromise;
        screenCaptureSnapshotCompleted.current = false;
        const { blob, width, height, sourceWidth, sourceHeight } =
          await controller.snapshot(abortController.signal);
        abortController.signal.throwIfAborted();
        if (!width || !height || !sourceWidth || !sourceHeight) {
          throw new Error('Captured snapshot has invalid dimensions');
        }
        screenCaptureSnapshotCompleted.current = true;

        const currentCanvas = nonNull(canvasRef.current);
        const scaleX = width / sourceWidth;
        const scaleY = height / sourceHeight;
        currentCanvas.discardActiveObject();
        currentCanvas.getObjects().forEach((object) => {
          (fabric.util as any).addTransformToObject(object, [
            scaleX,
            0,
            0,
            scaleY,
            0,
            0
          ]);
          object.setCoords();
        });
        currentCanvas.requestRenderAll();
        currentCanvas.fire(WEBSITE_HISTORY_REBASE_EVENT);

        setWebsitePageUrl(currentUrlRef.current);
        setSnapshotState({
          kind: 'object-url',
          url: URL.createObjectURL(blob),
          coord: { width, height }
        });
        const existingFiles = uppyRef.current.getFiles().filter((file) => {
          return file.name === WEBSITE_SNAPSHOT_ATTACHMENT_NAME;
        });
        uppyRef.current.removeFiles(existingFiles.map((file) => file.id));
        uppyRef.current.addFile({
          name: WEBSITE_SNAPSHOT_ATTACHMENT_NAME,
          type: 'image/png',
          data: blob
        });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        message.error({
          content: 'Something went wrong while taking a website snapshot.',
          className: 'message-dark-modal'
        });
        cancelAnnotations.current();
      } finally {
        if (snapshotTransitionIdRef.current === transitionId) {
          setTakingSnapshot(false);
        }
      }
    };
    takeSnapshot();
    return () => {
      abortController.abort();
      const currentCanvas = canvasRef.current;
      if (currentCanvas && snapshotTransitionIdRef.current === transitionId) {
        (currentCanvas as any)[WEBSITE_COORDINATE_TRANSITION_FLAG] = false;
      }
    };
  }, [
    snapshotRequested,
    startScreenCapture,
    setSnapshotState,
    setWebsitePageUrl
  ]);

  useLayoutEffect(() => {
    return () => {
      setSnapshot((prev) => {
        if (prev?.kind === 'object-url') {
          URL.revokeObjectURL(prev.url);
        }
        return null;
      });
    };
  }, []);

  const frameElement = (
    <div
      ref={frameRef}
      className={`media_viewer_website__container media_viewer_website__container--${viewport}`}
    >
      {grant.ready && !error ? (
        <div
          className={classNames('media_viewer_website__iframe_mount', {
            'media_viewer_website__iframe_mount--snapshot-disabled':
              highlightSnapshotDisabled
          })}
          ref={mountRef}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              contain: 'paint',
              background: snapshot ? '#1a1c1e' : 'transparent',
              pointerEvents: isNewComment || isDrawingOnCanvas ? 'auto' : 'none'
            }}
          >
            {snapshot && (
              <img
                src={snapshot.url}
                alt="Snapshot of the website"
                className="website-snapshot"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                onLoad={syncCanvasSize.current}
              />
            )}
            <div
              ref={canvasOverlayRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                visibility: loadingAttachmentSnapshot ? 'hidden' : 'visible',
                zIndex: 2
              }}
            >
              <FabricCanvas />
            </div>
          </div>
        </div>
      ) : (
        <PlaybackPreparingLoader
          error={!!grant.error || !!error}
          message={
            grant.error
              ? `Could not authorise the preview: ${grant.error}`
              : error || 'Authorising preview...'
          }
        />
      )}
    </div>
  );

  return {
    frameElement,
    faviconUrl,
    clearFavicon: () => setFaviconUrl(null),
    currentUrl: websitePageUrl,
    viewport,
    viewportAvailability,
    setViewport,
    canBack,
    canForward,
    isLoading,
    loadingButton,
    back,
    forward,
    reload,
    hardReload,
    openInNewTab,
    copyUrl,
    snapshot: !!snapshot,
    takingSnapshot,
    cancelSnapshot: () => {
      cancelAnnotations.current();
    }
  };
}
